import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApi, errMsg } from "@/lib/api";
import { toast } from "sonner";

// Warns (never blocks) when a save would push a phone over the daily call limit.
export function useCapGuard() {
  const api = useApi();
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const guard = async (patientId, medicines) => {
    let r;
    try { r = (await api.post("/cap-check", { patient_id: patientId, medicines })).data; } catch { return true; }
    if (!r.over) return true;
    return new Promise((res) => { resolver.current = res; setState({ ...r, patientId }); });
  };

  const done = (ok) => { setState(null); resolver.current?.(ok); };

  const merge = async () => {
    try {
      const p = (await api.get(`/patients/${state.patientId}`)).data;
      const call_times = { ...p.call_times };
      state.suggestions.forEach((s) => { call_times[s.slot] = s.to; });
      await api.put(`/patients/${state.patientId}`, { ...p, call_times });
      toast.success("Merged with the nearest call time");
      done(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const dialog = (
    <Dialog open={!!state} onOpenChange={(o) => !o && done(false)}>
      <DialogContent data-testid="cap-dialog">
        <DialogHeader><DialogTitle>Too many calls for this phone</DialogTitle></DialogHeader>
        {state && (
          <div className="grid gap-3 text-sm">
            <p data-testid="cap-warning">This makes {state.calls} calls a day for this phone. Merge with the nearest call time?</p>
            {state.suggestions.length ? state.suggestions.map((s) => (
              <p key={s.slot} className="rounded-xl bg-[var(--lav)] p-2" data-testid={`cap-suggestion-${s.slot}`}>Move the <b>{s.slot}</b> call from <b>{s.from}</b> to <b>{s.to}</b> ({s.minutes} min apart)</p>
            )) : <p className="text-muted-foreground" data-testid="cap-no-suggestion">No existing call time within 60 minutes to merge with.</p>}
            <p className="text-xs text-muted-foreground">The limit is {state.limit} a day. Nothing is lost — you decide.</p>
            <div className="flex flex-wrap gap-2">
              <Button disabled={!state.suggestions.length} onClick={merge} data-testid="cap-merge-btn">Merge</Button>
              <Button variant="outline" onClick={() => done(true)} data-testid="cap-keep-btn">Keep separate</Button>
              <Button variant="ghost" onClick={() => done(false)} data-testid="cap-cancel-btn">Cancel</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
  return { guard, dialog };
}
